import { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useSVGComposer } from '@/hooks/useSVGComposer';
import type { Tool } from '@/types/editor';
import { MIN_ZOOM, MAX_ZOOM, ZOOM_STEP } from '@/types/editor';
import type { Scrapbook, Page } from '@shared/types';

interface EditorCanvasProps {
  page: Page | undefined;
  scrapbook: Scrapbook;
  activeTool: Tool;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onContentChange: () => void;
}

export interface EditorCanvasRef {
  toJSON: () => string;
  toSVG: () => string;
  getElement: (id: string) => unknown;
  updateElement: (id: string, updates: Record<string, unknown>) => void;
}

// Map our tools to svg-composer tools
function mapTool(tool: Tool): 'select' | 'pan' | 'add-image' | 'add-text' | 'add-shape' {
  switch (tool) {
    case 'select':
      return 'select';
    case 'text':
      return 'add-text';
    case 'rectangle':
    case 'ellipse':
      return 'add-shape';
    case 'image':
      return 'add-image';
    default:
      return 'select';
  }
}

export const EditorCanvas = forwardRef<EditorCanvasRef, EditorCanvasProps>(
  function EditorCanvas(
    { page, scrapbook, activeTool, zoom, onZoomChange, onContentChange },
    ref
  ) {
    const containerRef = useRef<HTMLDivElement>(null);

    const {
      loadContent,
      getContent,
      getSVG,
      setTool,
      getElement,
      updateElement,
    } = useSVGComposer(containerRef, {
      width: scrapbook.pageWidth,
      height: scrapbook.pageHeight,
      onContentChange,
    });

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      toJSON: getContent,
      toSVG: getSVG,
      getElement,
      updateElement,
    }), [getContent, getSVG, getElement, updateElement]);

    // Load page content when page changes
    useEffect(() => {
      if (page?.svgContent) {
        loadContent(page.svgContent);
      }
    }, [page?.id, page?.svgContent, loadContent]);

    // Update tool when activeTool changes
    useEffect(() => {
      setTool(mapTool(activeTool));
    }, [activeTool, setTool]);

    // Zoom handlers
    const handleZoomIn = () => {
      onZoomChange(Math.min(zoom + ZOOM_STEP, MAX_ZOOM));
    };

    const handleZoomOut = () => {
      onZoomChange(Math.max(zoom - ZOOM_STEP, MIN_ZOOM));
    };

    const handleZoomReset = () => {
      onZoomChange(1);
    };

    // Wheel zoom
    const handleWheel = (e: React.WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        if (e.deltaY < 0) {
          handleZoomIn();
        } else {
          handleZoomOut();
        }
      }
    };

    return (
      <>
        <div className="editor-canvas-container" onWheel={handleWheel}>
          <div
            className="editor-canvas-wrapper"
            style={{
              width: scrapbook.pageWidth * zoom,
              height: scrapbook.pageHeight * zoom,
            }}
          >
            <div
              ref={containerRef}
              className="editor-canvas"
              style={{
                width: scrapbook.pageWidth,
                height: scrapbook.pageHeight,
                transform: `scale(${zoom})`,
                transformOrigin: 'top left',
              }}
            />
          </div>
        </div>

        <div className="editor-canvas-controls">
          <button
            className="editor-canvas-zoom-btn"
            onClick={handleZoomOut}
            disabled={zoom <= MIN_ZOOM}
            aria-label="Zoom out"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          <button
            className="editor-canvas-zoom-value"
            onClick={handleZoomReset}
            title="Reset zoom"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            className="editor-canvas-zoom-btn"
            onClick={handleZoomIn}
            disabled={zoom >= MAX_ZOOM}
            aria-label="Zoom in"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>
      </>
    );
  }
);
